import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Air,
  Checkroom,
  ExpandMore,
  FitnessCenter,
  Flare,
  Hotel,
  Kayaking,
  ScubaDiving,
  SelfImprovement,
  Timeline,
  Waves,
} from '@mui/icons-material';
import AppNavigation from './AppNavigation';
import SafetyNote from './SafetyNote';

type BreathPhase = {
  label: string;
  desc: string;
  color: string;
  seconds: number;
};

type Exercise = {
  name: string;
  sets: string;
  desc: string;
  diveLink: string;
};

type TrainingDay = {
  code: string;
  name: string;
  focus: string;
  tags: string[];
  exercises: Exercise[];
};

const BREATH_PHASES: BreathPhase[] = [
  { label: 'INHALE', desc: 'Breathe in', color: '#54d3e8', seconds: 4 },
  { label: 'HOLD', desc: 'Stay still', color: '#f0cf8a', seconds: 4 },
  { label: 'EXHALE', desc: 'Breathe out', color: '#b9f3ff', seconds: 4 },
  { label: 'HOLD', desc: 'Stay quiet', color: '#ffb35d', seconds: 4 },
];

const TRAINING_DAYS: TrainingDay[] = [
  {
    code: 'MON',
    name: 'Monday',
    focus: 'Breath + Deep Core',
    tags: ['breathing', 'core'],
    exercises: [
      {
        name: 'Box Breathing',
        sets: '5 rounds',
        desc: 'Sit tall or lie on your back. Inhale through your nose for 4 counts so the belly expands first, hold without tension, exhale for 4 counts with a slow controlled release, then hold empty for 4 counts.',
        diveLink: 'This is the breathing cadence you want on a regulator: calm, diaphragmatic, and deliberate so each breath does more work.',
      },
      {
        name: 'Dead Bug',
        sets: '3 x 8 each side',
        desc: 'Keep your lower back pinned to the floor while alternating opposite arm and leg extensions. Move slowly enough that your rib cage stays down and your hips do not rock.',
        diveLink: 'This trains the deep stabilizers that keep your hips from dropping and your trim from collapsing underwater.',
      },
      {
        name: 'Hollow Body Hold',
        sets: '3 x 20-30 sec',
        desc: 'Lift shoulders and legs slightly while pressing your lower back down. Hold the position with normal breathing and reduce the lever if you lose contact with the floor.',
        diveLink: 'It mirrors the long, streamlined body position you need to hold without constant fin correction.',
      },
    ],
  },
  {
    code: 'TUE',
    name: 'Tuesday',
    focus: 'Posterior Chain + Hip Stability',
    tags: ['strength', 'stability'],
    exercises: [
      {
        name: 'Bird Dog',
        sets: '3 x 10 each side',
        desc: 'Start on all fours, brace the trunk, then extend opposite arm and leg while keeping hips level. Pause briefly before returning with control.',
        diveLink: 'Anti-rotation strength helps when current hits from the side and you need to stay composed instead of fighting the water.',
      },
      {
        name: 'Glute Bridge',
        sets: '3 x 15',
        desc: 'Drive through the heels, lift hips until knees, hips, and shoulders align, then squeeze the glutes hard at the top before lowering slowly.',
        diveLink: 'A stronger posterior chain means more efficient kicking and less wasted gas from overworking the lower back and quads.',
      },
      {
        name: 'Side-Lying Hip Abduction',
        sets: '3 x 15 each side',
        desc: 'Keep hips stacked and the top leg straight. Raise the leg without rolling backward, then lower under control.',
        diveLink: 'Hip stability reduces fin flare and drag, which directly improves trim and lowers your SAC rate.',
      },
    ],
  },
  {
    code: 'WED',
    name: 'Wednesday',
    focus: 'Active Recovery + Extended Breath Work',
    tags: ['recovery', 'breathing'],
    exercises: [
      {
        name: 'Extended Box Breathing',
        sets: '8-10 rounds',
        desc: 'Keep the same sequence but lengthen each phase to 5 counts if control stays smooth. Relax the shoulders and make the exhale long and consistent.',
        diveLink: 'Long exhales are one of the simplest ways to reduce unnecessary gas burn, especially when stress starts creeping in.',
      },
      {
        name: 'Cat-Cow Mobility',
        sets: '2 x 10 slow reps',
        desc: 'Match every spinal movement to your breath. Inhale to open the front body, exhale to round and compress, moving slowly the whole time.',
        diveLink: 'Better spinal mobility and breath timing help you stay loose in gear and avoid stiffness that turns into wasted movement underwater.',
      },
      {
        name: 'Diaphragmatic Breathing Drill',
        sets: '5 min',
        desc: 'One hand on chest, one on belly. Keep the chest quiet while the belly rises on inhale and falls on a longer exhale.',
        diveLink: 'This reinforces belly-led breathing so your default pattern underwater is efficient instead of shallow and anxious.',
      },
    ],
  },
  {
    code: 'THU',
    name: 'Thursday',
    focus: 'Plank Variations + Lateral Core',
    tags: ['core', 'endurance'],
    exercises: [
      {
        name: 'Forearm Plank',
        sets: '3 x 30-45 sec',
        desc: 'Elbows under shoulders, heels driving back, glutes lightly engaged. Hold a straight line while breathing slowly and evenly.',
        diveLink: 'This builds the endurance to hold horizontal trim for the full dive, not just the first few minutes.',
      },
      {
        name: 'Side Plank',
        sets: '3 x 20-30 sec each side',
        desc: 'Stack shoulders and hips, then lift into one long line. Modify with the bottom knee down if needed, but keep the trunk rigid.',
        diveLink: 'It prevents unwanted rolling when you check gauges, signal a buddy, or adjust position mid-water.',
      },
      {
        name: 'Pallof Press',
        sets: '3 x 10 each side',
        desc: 'Stand sideways to a band or cable, brace, and press straight out while resisting twist. Pause briefly before returning.',
        diveLink: 'Anti-rotation control keeps your body shape stable when current or task loading tries to pull you off axis.',
      },
    ],
  },
  {
    code: 'FRI',
    name: 'Friday',
    focus: 'Full Integration',
    tags: ['integration', 'breathing', 'core'],
    exercises: [
      {
        name: 'Box Breathing + Dead Bug Combo',
        sets: '3 rounds',
        desc: 'Perform 4 rounds of breathing, then go straight into 8 controlled dead bugs per side. Keep your breathing quality steady between both pieces.',
        diveLink: 'You are training calm ventilation while the core is working, which is exactly the coordination a real dive demands.',
      },
      {
        name: 'Bear Crawl',
        sets: '3 x 20 m',
        desc: 'Knees hover off the ground, trunk stays flat, and opposite hand-foot pairs move slowly together. Treat it as controlled locomotion, not conditioning chaos.',
        diveLink: 'It sharpens full-body coordination so your movement underwater stays quiet and intentional rather than frantic.',
      },
      {
        name: 'Supine Leg Lowering',
        sets: '3 x 10',
        desc: 'Start with both legs vertical, keep the low back pressed down, and lower only as far as you can maintain trunk control before returning.',
        diveLink: 'Lower abdominal control is what keeps your legs from dropping and turning every dive into an uphill swim.',
      },
    ],
  },
  {
    code: 'SAT',
    name: 'Saturday',
    focus: 'Optional Dive or Swim',
    tags: ['application', 'optional'],
    exercises: [
      {
        name: 'Dive or Swim Session',
        sets: 'As desired',
        desc: 'Apply the full week. Stay aware of breathing rhythm, trim, and how much unnecessary leg or arm movement creeps in when task loaded.',
        diveLink: 'Track starting pressure, ending pressure, time, and average depth so you can compare SAC trends over the next 4 weeks.',
      },
    ],
  },
  {
    code: 'SUN',
    name: 'Sunday',
    focus: 'Full Rest',
    tags: ['rest'],
    exercises: [
      {
        name: 'Recovery Only',
        sets: 'Full day',
        desc: 'No structured session. Walk, stretch lightly, sleep more, and let adaptation catch up.',
        diveLink: 'Recovery matters because a relaxed diver usually breathes better, moves less, and handles stress more cleanly underwater.',
      },
    ],
  },
];

const TOTAL_TICKS = BREATH_PHASES.reduce((sum, phase) => sum + phase.seconds * 10, 0);
const PERIMETER = 560;

const insightCards = [
  {
    title: 'Trim and Position',
    text: 'Dead bugs, planks, and leg lowering sharpen the stabilizers that keep you horizontal without constantly sculling or bicycling.',
    icon: <Timeline sx={{ fontSize: 28 }} />,
  },
  {
    title: 'Diaphragm Control',
    text: 'The breathing block builds a slow belly-led rhythm you can carry directly onto a regulator to improve gas efficiency.',
    icon: <Air sx={{ fontSize: 28 }} />,
  },
  {
    title: 'Stress Response',
    text: 'Holding form and breathing on purpose under mild effort gives you a more useful response when something changes at depth.',
    icon: <Flare sx={{ fontSize: 28 }} />,
  },
  {
    title: 'Current Work',
    text: 'Hip stability and posterior-chain work reduce the flailing that spikes gas consumption when you need to hold station.',
    icon: <Waves sx={{ fontSize: 28 }} />,
  },
];

const tagColor = (tag: string) => {
  if (tag === 'rest') {
    return {
      color: '#ffcfb8',
      borderColor: 'rgba(255, 150, 107, 0.35)',
      backgroundColor: 'rgba(255, 150, 107, 0.12)',
    };
  }

  return {
    color: '#b8edf6',
    borderColor: 'rgba(84, 211, 232, 0.25)',
    backgroundColor: 'rgba(84, 211, 232, 0.10)',
  };
};

const BreathCoreTraining: React.FC = () => {
  const theme = useTheme();
  const [running, setRunning] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [phaseTicks, setPhaseTicks] = useState(0);
  const [rounds, setRounds] = useState(0);
  const [expandedDay, setExpandedDay] = useState<string>('MON');

  useEffect(() => {
    if (!running) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setPhaseTicks((currentTicks) => {
        const currentPhase = BREATH_PHASES[phaseIndex];
        const nextTicks = currentTicks + 1;

        if (nextTicks < currentPhase.seconds * 10) {
          return nextTicks;
        }

        setPhaseIndex((currentPhaseIndex) => {
          const nextPhaseIndex = currentPhaseIndex + 1;

          if (nextPhaseIndex >= BREATH_PHASES.length) {
            setRounds((currentRounds) => currentRounds + 1);
            return 0;
          }

          return nextPhaseIndex;
        });

        return 0;
      });
    }, 100);

    return () => window.clearInterval(interval);
  }, [phaseIndex, running]);

  const currentPhase = BREATH_PHASES[phaseIndex];
  const completedTicks = BREATH_PHASES.slice(0, phaseIndex).reduce((sum, phase) => sum + phase.seconds * 10, 0) + phaseTicks;
  const dashOffset = PERIMETER * (1 - completedTicks / TOTAL_TICKS);
  const remainingSeconds = Math.max(1, currentPhase.seconds - Math.floor(phaseTicks / 10));

  const handleToggleBreath = () => {
    setRunning((current) => !current);
  };

  const handleResetBreath = () => {
    setRunning(false);
    setPhaseIndex(0);
    setPhaseTicks(0);
    setRounds(0);
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', pb: 4 }}>
      <AppNavigation />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'radial-gradient(circle at 20% 10%, rgba(84,211,232,0.12), transparent 26%), radial-gradient(circle at 80% 20%, rgba(16,125,172,0.16), transparent 22%), radial-gradient(circle at 50% 100%, rgba(6,24,48,0.82), transparent 45%)',
        }}
      />

      {[...Array(12)].map((_, index) => (
        <Box
          key={index}
          sx={{
            position: 'absolute',
            bottom: `${-8 + index * 10}%`,
            left: `${6 + (index * 8) % 84}%`,
            width: 8 + (index % 4) * 6,
            height: 8 + (index % 4) * 6,
            borderRadius: '50%',
            border: '1px solid rgba(130, 226, 242, 0.18)',
            backgroundColor: 'rgba(130, 226, 242, 0.05)',
            opacity: 0.7,
          }}
        />
      ))}

      <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            px: { xs: 2.5, md: 4 },
            py: { xs: 3, md: 4 },
            borderRadius: 5,
            background: 'linear-gradient(145deg, rgba(5,18,34,0.94) 0%, rgba(8,45,76,0.88) 48%, rgba(6,22,42,0.96) 100%)',
            border: '1px solid rgba(84, 211, 232, 0.20)',
            boxShadow: '0 24px 80px rgba(3, 12, 28, 0.35)',
          }}
        >
          <Stack spacing={2}>
            <Chip
              label="Training Block"
              icon={<ScubaDiving />}
              sx={{
                alignSelf: 'flex-start',
                color: '#d1f5ff',
                backgroundColor: 'rgba(84,211,232,0.12)',
                border: '1px solid rgba(84,211,232,0.20)',
              }}
            />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.04em',
                  lineHeight: 0.95,
                  color: '#f2fbff',
                  maxWidth: 700,
                }}
              >
                Breath and Core
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mt: 1.5,
                  maxWidth: 760,
                  lineHeight: 1.6,
                  color: 'rgba(214, 242, 247, 0.82)',
                  fontWeight: 400,
                }}
              >
                A 4-week underwater-focused training cycle built to improve diaphragm control, trunk stability, trim, and measurable SAC performance.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              {[
                '5-10 min daily breathing',
                '7-day repeatable weekly plan',
                'Each movement tied to trim or SAC',
              ].map((label) => (
                <Chip
                  key={label}
                  label={label}
                  sx={{
                    color: '#edf7fa',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Box>

        <Card
          sx={{
            borderRadius: 5,
            border: '1px solid rgba(84, 211, 232, 0.16)',
            background: 'linear-gradient(150deg, rgba(10,33,56,0.96) 0%, rgba(7,19,34,0.95) 100%)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Stack spacing={1.25}>
                  <Typography variant="overline" sx={{ letterSpacing: '0.28em', color: '#7fdcec' }}>
                    Box Breathing Timer
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#f5fbff' }}>
                    4 in, 4 hold, 4 out, 4 hold
                  </Typography>
                  <Typography sx={{ lineHeight: 1.8, color: 'rgba(213, 238, 244, 0.78)' }}>
                    Use this before training or before a dive briefing to lower baseline tension, sharpen diaphragm control, and make calm breathing feel automatic.
                  </Typography>
                  <SafetyNote kind="breathwork" sx={{ color: 'rgba(213, 238, 244, 0.85)' }} />

                  <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="contained"
                      onClick={handleToggleBreath}
                      sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: 999,
                        fontWeight: 700,
                        color: '#042437',
                        background: 'linear-gradient(135deg, #74dceb 0%, #3db9d2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #88e6f3 0%, #4cc4dc 100%)',
                        },
                      }}
                    >
                      {running ? 'Pause' : rounds > 0 || phaseTicks > 0 ? 'Resume' : 'Start'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleResetBreath}
                      sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: 999,
                        borderColor: 'rgba(132, 220, 235, 0.28)',
                        color: '#d8f4f9',
                      }}
                    >
                      Reset
                    </Button>
                  </Stack>

                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Box>
                      <Typography variant="caption" sx={{ letterSpacing: '0.18em', color: 'rgba(174, 223, 232, 0.68)' }}>
                        Current phase
                      </Typography>
                      <Typography variant="h5" sx={{ color: currentPhase.color, fontWeight: 700 }}>
                        {currentPhase.label}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ letterSpacing: '0.18em', color: 'rgba(174, 223, 232, 0.68)' }}>
                        Rounds completed
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#ffcc77', fontWeight: 700 }}>
                        {rounds}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 320,
                  }}
                >
                  <Box sx={{ position: 'relative', width: 240, height: 240 }}>
                    <svg viewBox="0 0 200 200" width="240" height="240">
                      <rect x="30" y="30" width="140" height="140" rx="8" fill="none" stroke="rgba(116, 220, 235, 0.16)" strokeWidth="2" />
                      <circle cx="30" cy="30" r="5" fill="rgba(116, 220, 235, 0.7)" />
                      <circle cx="170" cy="30" r="5" fill="rgba(116, 220, 235, 0.7)" />
                      <circle cx="170" cy="170" r="5" fill="rgba(116, 220, 235, 0.7)" />
                      <circle cx="30" cy="170" r="5" fill="rgba(116, 220, 235, 0.7)" />
                      <rect
                        x="30"
                        y="30"
                        width="140"
                        height="140"
                        rx="8"
                        fill="none"
                        stroke={currentPhase.color}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray={PERIMETER}
                        strokeDashoffset={dashOffset}
                        style={{ filter: `drop-shadow(0 0 8px ${currentPhase.color})`, transition: 'stroke-dashoffset 0.1s linear, stroke 0.2s ease' }}
                      />
                    </svg>

                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="overline" sx={{ letterSpacing: '0.24em', color: currentPhase.color }}>
                        {currentPhase.desc}
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 800, color: '#f4fbff', lineHeight: 1 }}>
                        {remainingSeconds}
                      </Typography>
                      <Typography variant="h6" sx={{ letterSpacing: '0.2em', color: currentPhase.color, fontWeight: 700 }}>
                        {currentPhase.label}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box>
          <Typography variant="overline" sx={{ letterSpacing: '0.26em', color: theme.palette.primary.main }}>
            Weekly Plan
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, mb: 1, fontWeight: 700 }}>
            Repeat this 7-day cycle for 4 weeks
          </Typography>
          <Typography sx={{ mb: 3, maxWidth: 760, color: 'text.secondary', lineHeight: 1.8 }}>
            The goal is not random fitness. Each day builds either the breathing discipline, trunk stability, or movement economy that lowers workload underwater and improves post-dive gas calculations.
          </Typography>

          <Stack spacing={2}>
            {TRAINING_DAYS.map((day) => (
              <Accordion
                key={day.code}
                expanded={expandedDay === day.code}
                onChange={(_event, expanded) => setExpandedDay(expanded ? day.code : '')}
                disableGutters
                sx={{
                  background: 'rgba(10, 28, 46, 0.72)',
                  border: '1px solid rgba(84, 211, 232, 0.12)',
                  borderRadius: '20px !important',
                  overflow: 'hidden',
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#8edfed' }} />} sx={{ px: 2.5, py: 1.25 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#69d7e9', letterSpacing: '0.08em' }}>
                        {day.code}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#f3fbff' }}>
                        {day.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(202, 231, 238, 0.72)' }}>
                        {day.focus}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                        {day.tags.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                              ...tagColor(tag),
                              textTransform: 'uppercase',
                              letterSpacing: '0.12em',
                            }}
                          />
                        ))}
                      </Stack>
                    </Grid>
                  </Grid>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                  <Stack spacing={1.75}>
                    {day.exercises.map((exercise) => (
                      <Card
                        key={`${day.code}-${exercise.name}`}
                        sx={{
                          borderRadius: 3,
                          background: 'rgba(5, 17, 31, 0.76)',
                          border: '1px solid rgba(84, 211, 232, 0.08)',
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#f4fbff' }}>
                                {exercise.name}
                              </Typography>
                              <Chip
                                label={exercise.sets}
                                size="small"
                                icon={<FitnessCenter />}
                                sx={{
                                  alignSelf: 'flex-start',
                                  color: '#ffd58f',
                                  backgroundColor: 'rgba(255, 190, 98, 0.10)',
                                  border: '1px solid rgba(255, 190, 98, 0.18)',
                                }}
                              />
                            </Stack>

                            <Typography sx={{ lineHeight: 1.8, color: 'rgba(215, 239, 244, 0.82)' }}>
                              {exercise.desc}
                            </Typography>

                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'flex-start',
                                p: 1.5,
                                borderRadius: 3,
                                backgroundColor: 'rgba(255, 184, 77, 0.09)',
                                border: '1px solid rgba(255, 184, 77, 0.18)',
                              }}
                            >
                              <Checkroom sx={{ color: '#ffcb7d', mt: 0.1 }} />
                              <Typography variant="body2" sx={{ lineHeight: 1.7, color: '#f6d7a0' }}>
                                Dive connection: {exercise.diveLink}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Box>

        <Box>
          <Typography variant="overline" sx={{ letterSpacing: '0.26em', color: theme.palette.primary.main }}>
            Dive Connection
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, mb: 1, fontWeight: 700 }}>
            How the block pays off underwater
          </Typography>
          <Typography sx={{ mb: 3, maxWidth: 760, color: 'text.secondary', lineHeight: 1.8 }}>
            Expect the biggest improvements to show up as steadier trim, lower stress spikes, and cleaner gas-use notes after similar dives rather than as flashy gym numbers.
          </Typography>

          <Grid container spacing={2}>
            {insightCards.map((card) => (
              <Grid item xs={12} md={6} key={card.title}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(160deg, rgba(9,34,58,0.90) 0%, rgba(10,18,32,0.95) 100%)',
                    border: '1px solid rgba(84, 211, 232, 0.10)',
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.25}>
                      <Box sx={{ color: '#70dceb' }}>{card.icon}</Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#f5fbff' }}>
                        {card.title}
                      </Typography>
                      <Typography sx={{ lineHeight: 1.8, color: 'rgba(210, 236, 242, 0.78)' }}>
                        {card.text}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Card
          sx={{
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(8, 28, 46, 0.90) 0%, rgba(6, 19, 33, 0.95) 100%)',
            border: '1px solid rgba(84, 211, 232, 0.08)',
          }}
        >
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#f4fbff' }}>
                  Training note
                </Typography>
                <Typography sx={{ mt: 0.5, maxWidth: 760, color: 'rgba(210, 236, 242, 0.78)', lineHeight: 1.8 }}>
                  Run the cycle for 4 weeks, then compare dives of similar depth and workload. Look for lower stress, steadier trim, and improved gas use rather than chasing bigger workout volume too early.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip icon={<SelfImprovement />} label="Daily breath practice" />
                <Chip icon={<Kayaking />} label="Optional Saturday application" />
                <Chip icon={<Hotel />} label="Sunday recovery" />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default BreathCoreTraining;